import { makeAutoObservable, runInAction } from 'mobx'
import { notification } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { openApi } from '../../../helpers/request'
import i from '../../../translations/i'

// Воронка «доставлено → тап → заказ»: доставка — по устройствам, тапы — по пользователям,
// заказ засчитывается пушу с последним тапом, отменённые заказы считаются отдельно
export interface PushFunnelStats {
    sends: number
    delivered: number
    taps: number
    ctr: number | null
    orders: number
    canceled: number
    conversion: number | null
    revenue: number
}

export type PushFunnelSource = 'pushes' | 'templates'

/**
 * Воронка включается на сборку: REACT_APP_PUSH_FUNNEL_ENABLED=true в env окружения, где приложения
 * уже присылают тапы (POST pushes/tap). Без неё воронка показывается витриной «скоро»: без запросов,
 * цифр и фильтров. Флаг убрать, когда версии приложений с отправкой тапов выйдут везде
 */
export const PUSH_FUNNEL_ENABLED = process.env.REACT_APP_PUSH_FUNNEL_ENABLED === 'true'

const DEFAULT_DAYS = 30
const DATE_FORMAT = 'YYYY-MM-DD'

class PushFunnelController {

    source: PushFunnelSource

    period: [Dayjs, Dayjs] = [dayjs().subtract(DEFAULT_DAYS - 1, 'day'), dayjs()]
    timezone = ''

    available = true
    loading = false
    total: PushFunnelStats | null = null
    items: Record<number, PushFunnelStats> = {}

    attributionHours = 24
    savedAttributionHours = 24
    savingHours = false

    // С какого момента воронка собирает статистику (unix): null — отправок ещё не было,
    // undefined — бэкенд эту дату не отдаёт (старая версия)
    trackingSince: number | null | undefined = undefined

    constructor(source: PushFunnelSource) {
        this.source = source
        makeAutoObservable(this)
    }

    get periodQuery() {
        return `from=${this.period[0].format(DATE_FORMAT)}&to=${this.period[1].format(DATE_FORMAT)}`
    }

    stats(id: number): PushFunnelStats | undefined {
        return this.items[id]
    }

    setPeriod(dates: [Dayjs | null, Dayjs | null] | null) {
        if (!dates || !dates[0] || !dates[1]) return

        this.period = [dates[0], dates[1]]
        this.getSummary()
        if (this.source === 'templates') this.getTemplateItems()
    }

    getSummary() {
        if (!PUSH_FUNNEL_ENABLED) return

        this.loading = true

        openApi('get', `/pushes/funnel/summary?source=${this.source}&${this.periodQuery}`, {}, undefined, true)
            .then(result => {
                runInAction(() => {
                    const data = result.data
                    this.available = data.available
                    this.total = data.total
                    if ('tracking_since' in data) this.trackingSince = data.tracking_since
                    if (data.period) this.timezone = data.period.timezone
                    if (data.attribution_hours) {
                        this.attributionHours = data.attribution_hours
                        this.savedAttributionHours = data.attribution_hours
                    }
                })
            })
            .catch(error => console.log(error))
            .finally(() => runInAction(() => this.loading = false))
    }

    // Рассылки — по каждой за всё время: у рассылки одна отправка
    getPushItems(ids: number[]) {
        if (!PUSH_FUNNEL_ENABLED) return

        if (!ids.length) {
            this.items = {}
            return
        }

        openApi('get', `/pushes/funnel/items?source=pushes&ids=${ids.join(',')}`, {}, undefined, true)
            .then(result => runInAction(() => this.items = this.byId(result.data.items)))
            .catch(error => console.log(error))
    }

    getTemplateItems() {
        if (!PUSH_FUNNEL_ENABLED) return

        openApi('get', `/pushes/funnel/items?source=templates&${this.periodQuery}`, {}, undefined, true)
            .then(result => runInAction(() => this.items = this.byId(result.data.items)))
            .catch(error => console.log(error))
    }

    setAttributionHours(value: number | null) {
        if (value !== null) this.attributionHours = value
    }

    saveAttributionHours() {
        const data = new FormData()
        data.append('push_attribution_hours', `${this.attributionHours}`)

        this.savingHours = true

        openApi('post', 'settings', data)
            .then(() => {
                runInAction(() => this.savedAttributionHours = this.attributionHours)
                notification.success({
                    message: i.t('def:success.complete'),
                    description: i.t('def:success.edit')
                })
            })
            .catch(err => {
                notification.error({
                    message: i.t('def:errors.error'),
                    description: err.response?.data?.data?.message ?? i.t('def:errors.somethingWentWrong')
                })
            })
            .finally(() => runInAction(() => this.savingHours = false))
    }

    private byId(items: ({ id: number } & PushFunnelStats)[] = []) {
        const result: Record<number, PushFunnelStats> = {}
        items.forEach(item => result[item.id] = item)
        return result
    }
}

export const pushesFunnel = new PushFunnelController('pushes')
export const templatesFunnel = new PushFunnelController('templates')
